import {
  CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS,
  STOREFRONT_CART_ATTRIBUTES,
} from "@tablescape/constants";
import {
  type AddToCartMetadata,
  type CartGiftWithPurchaseConnection,
  type GwpOffer,
  type SpendingGwpTierConfig,
} from "@tablescape/state";
import {
  type CartLineLike,
  findRelatedCartSpendingGiftWithPurchaseLine,
  getGiftWithPurchaseAttributeValue,
  isCartInReplaceLineBundleTransformGap,
  isCartLineExcludedFromThreshold,
} from "./cart";
import {
  getProductGwpGiftLinesForParent,
  getProductGwpGiftLinesForVariant,
  getProductGwpTotalParentQuantityForVariant,
  isLegacyProductGwpGiftLink,
  isProductGwpGiftLine,
  isProductGwpParentLineCandidate,
  parseProductGwpGiftLink,
  sumProductGwpGiftLineQuantities,
} from "./productGwpCartLines";
import { getShopifyGlobalId } from "./general";
import { getLoyaltyTier } from "./customer";
import {
  gwpInteractionKeyForOffer,
  readGwpInteraction,
  readGwpInteractions,
} from "./gwpInteractionStore";

const normalizeCartLinesArray = (
  lines: Array<CartLineLike | null | undefined> | null | undefined
): CartLineLike[] | undefined => {
  if (lines == null) {
    return undefined;
  }

  return lines.filter((line): line is CartLineLike => line != null);
};

const GWP_DISCOUNT_KEYWORDS = ["GWP", "THRESHOLD"];

const isFullPriceGwpDiscount = (
  discountAllocations: CartLineLike["discountAllocations"],
  lineSubtotal: string | undefined
): boolean => {
  if (!discountAllocations?.length || !lineSubtotal) {
    return false;
  }

  const lineAmount = Number(lineSubtotal);
  if (lineAmount <= 0) {
    return false;
  }

  return discountAllocations.some((allocation) => {
    if (
      !allocation ||
      !("title" in allocation) ||
      typeof allocation.title !== "string"
    ) {
      return false;
    }

    const upper = allocation.title.toUpperCase();
    const matchesKeyword = GWP_DISCOUNT_KEYWORDS.some((keyword) =>
      upper.includes(keyword)
    );

    if (!matchesKeyword) {
      return false;
    }

    const discountAmount = Number(allocation.discountedAmount?.amount ?? 0);
    return discountAmount >= lineAmount;
  });
};

export const getCartSpendingAmountExcludingGwpGifts = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  excludedProductIds?: readonly string[]
): number =>
  lines?.reduce((sum, line) => {
    if (!line?.cost?.totalAmount?.amount) {
      return sum;
    }

    if (isCartLineExcludedFromThreshold(line, excludedProductIds)) {
      return sum;
    }

    const hasGwpAttribute = getGiftWithPurchaseAttributeValue(
      line,
      CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.SPENDING_THRESHOLD
    );

    if (hasGwpAttribute) {
      return sum;
    }

    const hasGwpDiscount = isFullPriceGwpDiscount(
      line.discountAllocations,
      line.cost.subtotalAmount?.amount ?? undefined
    );

    if (hasGwpDiscount) {
      return sum;
    }

    return sum + Number(line.cost.totalAmount.amount);
  }, 0) ?? 0;

const parseSpendingThresholdAmount = (
  spendingThreshold: string | undefined
): number | null => {
  const normalized = spendingThreshold?.trim();
  if (!normalized) {
    return null;
  }

  const amount = Number(normalized);
  return Number.isFinite(amount) && amount > 0 ? amount : null;
};

export const getEligibleSpendingGwpTierConfigs = (
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined,
  cartSpendingAmountExcludingGwpGifts: number,
  options?: { loyaltyTier?: string | null }
): SpendingGwpTierConfig[] => {
  const loyaltyTier =
    options?.loyaltyTier !== undefined ? options.loyaltyTier : getLoyaltyTier();

  return (spendingGwpTierConfigs ?? []).filter((config) => {
    const thresholdAmount = parseSpendingThresholdAmount(
      config.spendingThreshold
    );

    if (
      thresholdAmount == null ||
      !(config.productIds?.length || config.autoAddProductIds?.length) ||
      cartSpendingAmountExcludingGwpGifts < thresholdAmount
    ) {
      return false;
    }

    return isGwpOfferLoyaltyEligible(
      mapSpendingGwpTierConfigToGwpOffer(config),
      loyaltyTier
    );
  });
};

export const isGwpOfferLoyaltyEligible = (
  offer: GwpOffer,
  loyaltyTier: string | null = getLoyaltyTier()
): boolean => {
  const tiers = offer.eligibleLoyaltyTiers;
  return !tiers?.length || (!!loyaltyTier && tiers.includes(loyaltyTier));
};

export const isGwpOfferPickYourOwnPromptEligible = (
  offer: GwpOffer,
  loyaltyTier: string | null = getLoyaltyTier()
): boolean =>
  isGwpOfferLoyaltyEligible(offer, loyaltyTier) &&
  offer.giftProductIds.length > 0 &&
  offer.autoAddProductIds.length === 0;

export const getSpendingGwpGiftLinesForThreshold = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingThreshold: string
): CartLineLike[] =>
  (normalizeCartLinesArray(lines) ?? []).filter(
    (line) =>
      getGiftWithPurchaseAttributeValue(
        line,
        CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.SPENDING_THRESHOLD
      ) === spendingThreshold
  );

export const countSpendingGwpGiftsForThreshold = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingThreshold: string
): number =>
  getSpendingGwpGiftLinesForThreshold(lines, spendingThreshold).reduce(
    (total, line) =>
      total +
      (typeof line.quantity === "number" && line.quantity > 0
        ? line.quantity
        : 1),
    0
  );

const isAutoAddOfferFullyInCart = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  offer: GwpOffer
): boolean => {
  switch (offer.kind) {
    case "spending": {
      const giftMerchandiseIds = new Set(
        getSpendingGwpGiftLinesForThreshold(
          lines,
          offer.trigger.spendingThreshold
        ).flatMap((line) => (line.merchandise?.id ? [line.merchandise.id] : []))
      );

      return giftMerchandiseIds.size >= offer.autoAddProductIds.length;
    }
    case "product":
      return isGwpOfferSelectionComplete(lines, offer);
    default: {
      const _exhaustive: never = offer;
      return _exhaustive;
    }
  }
};

const isUnresolvedGwpPromptOffer = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  offer: GwpOffer,
  loyaltyTier: string | null
): boolean => {
  if (offer.autoAddProductIds.length > 0) {
    return (
      isGwpOfferLoyaltyEligible(offer, loyaltyTier) &&
      readGwpInteraction(gwpInteractionKeyForOffer(offer))?.action ===
        "removed" &&
      !isAutoAddOfferFullyInCart(lines, offer)
    );
  }

  return (
    isGwpOfferPickYourOwnPromptEligible(offer, loyaltyTier) &&
    !isGwpOfferSelectionComplete(lines, offer)
  );
};

export const cartHasUnresolvedGwpPromptOffers = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined,
  options?: {
    buttonText?: string;
    excludedFromTieredProgressBarProductIds?: readonly string[];
    ignorePickYourOwnPrompts?: boolean;
    loyaltyTier?: string | null;
  }
): boolean => {
  const loyaltyTier =
    options?.loyaltyTier !== undefined ? options.loyaltyTier : getLoyaltyTier();
  const spendingAmount = getCartSpendingAmountExcludingGwpGifts(
    lines,
    options?.excludedFromTieredProgressBarProductIds
  );
  const offers = [
    ...getEligibleSpendingGwpTierConfigs(
      spendingGwpTierConfigs,
      spendingAmount,
      {
        loyaltyTier,
      }
    ).map(mapSpendingGwpTierConfigToGwpOffer),
    ...getProductGwpOffersFromCartLines(lines, options),
  ];

  return offers.some((offer) => {
    if (options?.ignorePickYourOwnPrompts && !offer.autoAddProductIds.length) {
      return false;
    }

    return isUnresolvedGwpPromptOffer(lines, offer, loyaltyTier);
  });
};

const cartHasSpendingGwpGiftLines = (
  lines: Array<CartLineLike | null | undefined> | null | undefined
): boolean =>
  lines?.some(
    (line) =>
      line != null &&
      !!getGiftWithPurchaseAttributeValue(
        line,
        CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS.SPENDING_THRESHOLD
      )
  ) ?? false;

const cartHasProductGwpGiftLines = (
  lines: Array<CartLineLike | null | undefined> | null | undefined
): boolean => lines?.some((line) => isProductGwpGiftLine(line)) ?? false;

const countEligibleProductGwpParentLines = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  options?: { loyaltyTier?: string | null }
): number =>
  lines?.filter(
    (line) =>
      line != null &&
      isProductGwpParentLineCandidate(line) &&
      mapProductCartLineToGwpOffer(line, options) !== null
  ).length ?? 0;

const hasEligibleProductGwpParentForVariant = (
  parentVariantId: string,
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  options?: { loyaltyTier?: string | null }
): boolean =>
  lines?.some(
    (line) =>
      line != null &&
      line.merchandise?.id === parentVariantId &&
      isProductGwpParentLineCandidate(line) &&
      mapProductCartLineToGwpOffer(line, options) !== null
  ) ?? false;

export const isProductGwpGiftLineIneligibleForCleanup = (
  giftLine: CartLineLike | null | undefined,
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  options?: { loyaltyTier?: string | null }
): boolean => {
  if (!giftLine?.id || !isProductGwpGiftLine(giftLine)) {
    return false;
  }

  const giftAttribute = giftLine.attributes?.find(
    (attribute) =>
      attribute?.key === STOREFRONT_CART_ATTRIBUTES.GIFT_WITH_PURCHASE
  );
  const link = parseProductGwpGiftLink(giftAttribute?.value);
  if (!link) {
    return false;
  }

  if (isLegacyProductGwpGiftLink(link)) {
    return countEligibleProductGwpParentLines(lines, options) === 0;
  }

  if (link.cartLineId) {
    const parentLine = lines?.find((line) => line?.id === link.cartLineId);
    if (parentLine) {
      if (mapProductCartLineToGwpOffer(parentLine, options) !== null) {
        return false;
      }

      if (link.parentVariantId) {
        return !hasEligibleProductGwpParentForVariant(
          link.parentVariantId,
          lines,
          options
        );
      }

      return true;
    }

    if (link.parentVariantId) {
      return !hasEligibleProductGwpParentForVariant(
        link.parentVariantId,
        lines,
        options
      );
    }

    return true;
  }

  if (link.parentVariantId) {
    return !hasEligibleProductGwpParentForVariant(
      link.parentVariantId,
      lines,
      options
    );
  }

  return false;
};

export const getCartGwpUiState = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined,
  options?: {
    buttonText?: string;
    cartMetadata?: AddToCartMetadata | null;
    excludedFromTieredProgressBarProductIds?: readonly string[];
    loyaltyTier?: string | null;
    tieredProgressBarEnabled?: boolean;
  }
): {
  hasGwpContentBelow: boolean;
  mountGwpManager: boolean;
  showGwpSection: boolean;
} => {
  const hasGwpContentBelow = cartHasUnresolvedGwpPromptOffers(
    lines,
    spendingGwpTierConfigs,
    options
  );

  return {
    hasGwpContentBelow,
    mountGwpManager: cartNeedsGwpManager(
      lines,
      spendingGwpTierConfigs,
      options
    ),
    showGwpSection: !!options?.tieredProgressBarEnabled || hasGwpContentBelow,
  };
};

const cartHasAutoAddInteractionToReset = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined,
  options?: { buttonText?: string; loyaltyTier?: string | null }
): boolean => {
  const interactionKeys = Object.keys(readGwpInteractions());
  if (interactionKeys.length === 0) {
    return false;
  }

  const activeKeys = new Set(
    getEligibleAutoAddGwpOffers(lines, spendingGwpTierConfigs, options).map(
      gwpInteractionKeyForOffer
    )
  );

  return interactionKeys.some((key) => !activeKeys.has(key));
};

/** Offers that can auto-add right now: eligible spending tiers plus product parents, each with auto-add products configured. */
export const getEligibleAutoAddGwpOffers = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined,
  options?: {
    buttonText?: string;
    excludedFromTieredProgressBarProductIds?: readonly string[];
    loyaltyTier?: string | null;
  }
): GwpOffer[] => {
  const loyaltyTier =
    options?.loyaltyTier !== undefined ? options.loyaltyTier : getLoyaltyTier();
  const spendingOffers = getEligibleSpendingGwpTierConfigs(
    spendingGwpTierConfigs,
    getCartSpendingAmountExcludingGwpGifts(
      lines,
      options?.excludedFromTieredProgressBarProductIds
    ),
    { loyaltyTier }
  ).map(mapSpendingGwpTierConfigToGwpOffer);

  return [
    ...spendingOffers,
    ...getProductGwpOffersFromCartLines(lines, options),
  ].filter(
    (offer) =>
      offer.autoAddProductIds.length > 0 &&
      isGwpOfferLoyaltyEligible(offer, loyaltyTier)
  );
};

export const cartNeedsGwpManager = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  spendingGwpTierConfigs: SpendingGwpTierConfig[] | undefined,
  options?: {
    buttonText?: string;
    cartMetadata?: AddToCartMetadata | null;
    excludedFromTieredProgressBarProductIds?: readonly string[];
    loyaltyTier?: string | null;
    tieredProgressBarEnabled?: boolean;
  }
): boolean => {
  if (options?.tieredProgressBarEnabled) {
    return true;
  }

  if (
    isCartInReplaceLineBundleTransformGap({
      cartMetadata: options?.cartMetadata,
      lines,
    })
  ) {
    return true;
  }

  const loyaltyTier =
    options?.loyaltyTier !== undefined ? options.loyaltyTier : getLoyaltyTier();

  if (
    cartHasUnresolvedGwpPromptOffers(lines, spendingGwpTierConfigs, options)
  ) {
    return true;
  }

  const spendingAmount = getCartSpendingAmountExcludingGwpGifts(
    lines,
    options?.excludedFromTieredProgressBarProductIds
  );

  for (const config of getEligibleSpendingGwpTierConfigs(
    spendingGwpTierConfigs,
    spendingAmount,
    { loyaltyTier }
  )) {
    if (!config.autoAddProductIds?.length) {
      continue;
    }

    const offer = mapSpendingGwpTierConfigToGwpOffer(config);
    if (!isGwpOfferLoyaltyEligible(offer, loyaltyTier)) {
      continue;
    }

    if (!isGwpOfferSelectionComplete(lines, offer)) {
      return true;
    }
  }

  for (const offer of getProductGwpOffersFromCartLines(lines, options)) {
    if (offer.kind !== "product" || offer.autoAddProductIds.length === 0) {
      continue;
    }

    if (!isGwpOfferLoyaltyEligible(offer, loyaltyTier)) {
      continue;
    }

    if (!isGwpOfferSelectionComplete(lines, offer)) {
      return true;
    }
  }

  return (
    cartHasSpendingGwpGiftLines(lines) ||
    cartHasProductGwpGiftLines(lines) ||
    cartHasAutoAddInteractionToReset(lines, spendingGwpTierConfigs, options)
  );
};

type MerchandiseGwpReference = NonNullable<
  NonNullable<CartGiftWithPurchaseConnection["reference"]>
>;

type ParsedEligibleLoyaltyTiers =
  | { status: "unrestricted" }
  | { status: "restricted"; tiers: string[] }
  | { status: "invalid" };

/**
 * @type_assertion_justification
 * TECHNICAL REASON: Reflect.get returns object after runtime guards; GraphQL
 * metaobject reference shape is validated by downstream field reads.
 * RUNTIME GUARANTEE: Non-null object checks precede the assertion.
 * ALTERNATIVE CONSIDERED: Full metaobject type guard — rejected as brittle
 * against partial cart fragment payloads.
 * RISK ASSESSMENT: Missing fields yield empty strings/arrays in mappers.
 * MIGRATION_PLAN: Narrow via schema guard if GWP metaobject shape stabilizes.
 */
type UNSAFE_ASSERTION__MerchandiseGwpReference = MerchandiseGwpReference;

const getMerchandiseGwpReference = (
  merchandise: CartLineLike["merchandise"]
): MerchandiseGwpReference | undefined => {
  if (merchandise == null || typeof merchandise !== "object") {
    return undefined;
  }

  const giftWithPurchase = Reflect.get(merchandise, "giftWithPurchase");
  if (giftWithPurchase == null || typeof giftWithPurchase !== "object") {
    return undefined;
  }

  const reference = Reflect.get(giftWithPurchase, "reference");
  if (reference == null || typeof reference !== "object") {
    return undefined;
  }

  return reference as UNSAFE_ASSERTION__MerchandiseGwpReference;
};

export const resolveMetaobjectGiftBadgeText = (
  reference: MerchandiseGwpReference | null | undefined
): string => {
  const value = reference?.giftBadgeText?.value;
  return typeof value === "string" ? value : "";
};

export const resolveProductGwpGiftBadgeText = (
  reference: MerchandiseGwpReference | null | undefined,
  defaultGiftBadgeText: string
): string => resolveMetaobjectGiftBadgeText(reference) || defaultGiftBadgeText;

export const resolveProductGwpGiftBadgeTextForMerchandise = (
  merchandise: CartLineLike["merchandise"],
  defaultGiftBadgeText: string
): string =>
  resolveProductGwpGiftBadgeText(
    getMerchandiseGwpReference(merchandise),
    defaultGiftBadgeText
  );

const parseEligibleLoyaltyTiers = (
  raw: MerchandiseGwpReference["eligibleLoyaltyTiers"]
): ParsedEligibleLoyaltyTiers => {
  const value = raw?.value;
  if (!value) return { status: "unrestricted" };
  if (Array.isArray(value)) {
    return {
      status: "restricted",
      tiers: value.filter((tier): tier is string => typeof tier === "string"),
    };
  }
  if (typeof value !== "string") return { status: "invalid" };
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return { status: "invalid" };
    return {
      status: "restricted",
      tiers: parsed.filter((tier): tier is string => typeof tier === "string"),
    };
  } catch {
    return { status: "invalid" };
  }
};

const isCustomerEligibleForLoyaltyTiers = (
  parsed: ParsedEligibleLoyaltyTiers,
  loyaltyTier: string | null
): boolean => {
  switch (parsed.status) {
    case "invalid":
      return false;
    case "unrestricted":
      return true;
    case "restricted":
      if (!parsed.tiers.length) return false;
      if (loyaltyTier === null) return false;
      return parsed.tiers.includes(loyaltyTier);
    default: {
      const _exhaustive: never = parsed;
      return _exhaustive;
    }
  }
};

const toOfferEligibleLoyaltyTiers = (
  parsed: ParsedEligibleLoyaltyTiers
): string[] | null => (parsed.status === "restricted" ? parsed.tiers : null);

const getReferenceProductIds = (field: unknown): string[] => {
  if (field == null || typeof field !== "object" || !("references" in field)) {
    return [];
  }

  const references = field.references;
  if (references == null || typeof references !== "object") return [];

  const nodes = "nodes" in references ? references.nodes : undefined;
  if (!Array.isArray(nodes)) return [];

  return nodes
    .map((node) =>
      node != null && typeof node === "object" && "id" in node
        ? node.id
        : undefined
    )
    .filter((id): id is string => typeof id === "string");
};

export const mapSpendingGwpTierConfigToGwpOffer = (
  config: SpendingGwpTierConfig
): GwpOffer => ({
  autoAddProductIds: config.autoAddProductIds ?? [],
  buttonText: config.buttonText,
  eligibleLoyaltyTiers: config.eligibleLoyaltyTiers ?? null,
  giftItemBadgeText: config.giftItemBadgeText,
  giftProductIds: config.productIds ?? [],
  id: config.spendingThreshold,
  kind: "spending",
  title: config.title,
  trigger: {
    type: "threshold",
    spendingThreshold: config.spendingThreshold,
  },
});

export const mapProductCartLineToGwpOffer = (
  line: CartLineLike,
  options?: { buttonText?: string; loyaltyTier?: string | null }
): GwpOffer | null => {
  if (!line.id || !line.merchandise?.id) return null;

  const reference = getMerchandiseGwpReference(line.merchandise);
  if (!reference) return null;

  const parsedEligibleLoyaltyTiers = parseEligibleLoyaltyTiers(
    reference.eligibleLoyaltyTiers
  );
  const loyaltyTier =
    options?.loyaltyTier !== undefined ? options.loyaltyTier : getLoyaltyTier();

  if (
    !isCustomerEligibleForLoyaltyTiers(parsedEligibleLoyaltyTiers, loyaltyTier)
  ) {
    return null;
  }

  const eligibleLoyaltyTiers = toOfferEligibleLoyaltyTiers(
    parsedEligibleLoyaltyTiers
  );

  const autoAddProductIds = getReferenceProductIds(reference.autoAddProducts);
  const giftProductIds = getReferenceProductIds(reference.products);
  const hasConfiguredGifts =
    autoAddProductIds.length > 0 || giftProductIds.length > 0;

  if (!hasConfiguredGifts) return null;

  const title = reference.title?.value;
  if (!title) return null;

  return {
    autoAddProductIds,
    buttonText: options?.buttonText ?? "",
    eligibleLoyaltyTiers,
    giftItemBadgeText: resolveMetaobjectGiftBadgeText(reference),
    giftProductIds,
    id: line.id,
    kind: "product",
    title,
    trigger: {
      type: "parentLine",
      cartLineId: line.id,
      parentVariantId: line.merchandise.id,
    },
  };
};

export const getProductGwpOffersFromCartLines = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  options?: { buttonText?: string; loyaltyTier?: string | null }
): GwpOffer[] => {
  if (!lines?.length) return [];

  const offersByVariantId = new Map<string, GwpOffer>();

  for (const line of lines) {
    if (line == null || !isProductGwpParentLineCandidate(line)) {
      continue;
    }

    const offer = mapProductCartLineToGwpOffer(line, options);
    if (!offer || offer.kind !== "product") {
      continue;
    }

    const { parentVariantId } = offer.trigger;
    if (!offersByVariantId.has(parentVariantId)) {
      offersByVariantId.set(parentVariantId, offer);
    }
  }

  return Array.from(offersByVariantId.values());
};

export const countProductGwpGiftsForParentLine = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentCartLineId: string
): number =>
  sumProductGwpGiftLineQuantities(
    getProductGwpGiftLinesForParent(
      normalizeCartLinesArray(lines),
      parentCartLineId
    )
  );

export const countProductGwpGiftsForVariant = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentVariantId: string
): number =>
  sumProductGwpGiftLineQuantities(
    getProductGwpGiftLinesForVariant(
      normalizeCartLinesArray(lines),
      parentVariantId
    )
  );

export const getProductGwpGiftQuantitiesByMerchandiseId = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentVariantId: string
): Map<string, number> => {
  const quantityByMerchandiseId = new Map<string, number>();

  for (const giftLine of getProductGwpGiftLinesForVariant(
    normalizeCartLinesArray(lines),
    parentVariantId
  )) {
    const merchandiseId = giftLine.merchandise?.id;
    if (!merchandiseId) {
      continue;
    }

    const quantity =
      typeof giftLine.quantity === "number" && giftLine.quantity > 0
        ? giftLine.quantity
        : 1;
    quantityByMerchandiseId.set(
      merchandiseId,
      (quantityByMerchandiseId.get(merchandiseId) ?? 0) + quantity
    );
  }

  return quantityByMerchandiseId;
};

export const countProductGwpGiftsForVariantAndMerchandise = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentVariantId: string,
  giftMerchandiseId: string
): number =>
  getProductGwpGiftQuantitiesByMerchandiseId(lines, parentVariantId).get(
    giftMerchandiseId
  ) ?? 0;

const isMultiAutoAddProductGwpOfferSelectionComplete = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  offer: Extract<GwpOffer, { kind: "product" }>
): boolean => {
  const requiredGiftCount = getProductGwpRequiredGiftCountForVariant(
    lines,
    offer.trigger.parentVariantId
  );
  const satisfiedAutoAddGiftCount = Array.from(
    getProductGwpGiftQuantitiesByMerchandiseId(
      lines,
      offer.trigger.parentVariantId
    ).values()
  ).filter((quantity) => quantity >= requiredGiftCount).length;

  return satisfiedAutoAddGiftCount >= offer.autoAddProductIds.length;
};

export const getProductGwpRequiredGiftCount = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentCartLineId: string
): number => {
  const parentLine = lines?.find((line) => line?.id === parentCartLineId);
  const quantity = parentLine?.quantity;

  return typeof quantity === "number" && quantity > 0 ? quantity : 1;
};

export const getProductGwpRequiredGiftCountForVariant = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentVariantId: string
): number =>
  getProductGwpTotalParentQuantityForVariant(
    normalizeCartLinesArray(lines),
    parentVariantId
  );

export const isProductGwpOfferSelectionComplete = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentCartLineId: string
): boolean =>
  countProductGwpGiftsForParentLine(lines, parentCartLineId) >=
  getProductGwpRequiredGiftCount(lines, parentCartLineId);

export const isProductGwpOfferSelectionCompleteForVariant = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentVariantId: string
): boolean =>
  countProductGwpGiftsForVariant(lines, parentVariantId) >=
  getProductGwpRequiredGiftCountForVariant(lines, parentVariantId);

export const formatProductGwpPromptSlotTitle = (
  title: string,
  slotIndex: number,
  total: number
): string => (total > 1 ? `${title} (${slotIndex}/${total})` : title);

const buildUnassignedPromptSlots = (
  total: number,
  selected: number
): Array<{ slotIndex: number; total: number }> => {
  const unassigned = Math.max(0, total - selected);

  return Array.from({ length: unassigned }, (_, index) => ({
    slotIndex: selected + index + 1,
    total,
  }));
};

export const getProductGwpUnassignedPromptSlots = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentCartLineId: string
): Array<{ slotIndex: number; total: number }> =>
  buildUnassignedPromptSlots(
    getProductGwpRequiredGiftCount(lines, parentCartLineId),
    countProductGwpGiftsForParentLine(lines, parentCartLineId)
  );

export const getProductGwpUnassignedPromptSlotsForVariant = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  parentVariantId: string
): Array<{ slotIndex: number; total: number }> =>
  buildUnassignedPromptSlots(
    getProductGwpRequiredGiftCountForVariant(lines, parentVariantId),
    countProductGwpGiftsForVariant(lines, parentVariantId)
  );

export const isGwpOfferSelectionComplete = (
  lines: Array<CartLineLike | null | undefined> | null | undefined,
  offer: GwpOffer
): boolean => {
  switch (offer.kind) {
    case "spending":
      return !!findRelatedCartSpendingGiftWithPurchaseLine(
        lines,
        offer.trigger.spendingThreshold
      );
    case "product":
      if (offer.autoAddProductIds.length > 1) {
        return isMultiAutoAddProductGwpOfferSelectionComplete(lines, offer);
      }

      return isProductGwpOfferSelectionCompleteForVariant(
        lines,
        offer.trigger.parentVariantId
      );
    default: {
      const _exhaustive: never = offer;
      return _exhaustive;
    }
  }
};

export const normalizeGwpProductId = (id: string | number): string => {
  const normalizedId = String(id);

  return normalizedId.startsWith("gid://")
    ? normalizedId
    : getShopifyGlobalId("Product", normalizedId);
};
